import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  CalendarBlank, Link as LinkIcon, GoogleLogo,
  ArrowSquareOut, CalendarCheck, Clock,
  Copy, Check, ArrowsClockwise, Warning,
} from '@phosphor-icons/react';
import { fetchIcalToken, regenerateIcalToken } from '@/lib/meApi';
import {
  fetchCalendarSyncStatus, enableCalendarSync, disableCalendarSync, resyncCalendar,
  type CalendarSyncProviderId, type CalendarSyncProviderStatus,
} from '@/lib/calendarSyncApi';
import { toast } from 'sonner';
import { beginOAuthConnect } from '@/lib/mailboxApi';
import { getApiErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { OAuthReturnNotice } from '@/components/shared/OAuthReturnNotice';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { BackButton } from '@/components/shared/BackButton';

// ─── Helpers ──────────────────────────────────────────────────

function formatExpiry(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function SectionHead({ icon, title, desc, right, badge }: {
  icon: React.ReactNode;
  title: string;
  desc?: string;
  right?: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3 border-b border-border/50 pb-4">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </div>
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            {title}
            {badge}
          </div>
          {desc && <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>}
        </div>
      </div>
      {right}
    </div>
  );
}

// ─── iCal subscription section ───────────────────────────────

function IcalSection() {
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['ical-token'],
    queryFn: fetchIcalToken,
    staleTime: Infinity,
  });

  const regenMutation = useMutation({
    mutationFn: regenerateIcalToken,
    onSuccess: (newData) => {
      qc.setQueryData(['ical-token'], newData);
      setConfirmRegen(false);
    },
  });

  function copyUrl() {
    if (!data?.url) return;
    navigator.clipboard.writeText(data.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="rounded-2xl border border-border/60 bg-card p-6">
      <SectionHead
        icon={<CalendarBlank size={14} weight="bold" />}
        title="Subscribe to Your Diary"
        desc="Add your Jurivoxa diary to Google Calendar, Apple Calendar, or Outlook — updates automatically"
        badge={
          <span className="rounded-full bg-emerald-100 dark:bg-emerald-500/15 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
            Live
          </span>
        }
      />

      <p className="mb-4 text-sm text-muted-foreground leading-relaxed">
        Copy the URL below and add it to any calendar app. Hearings, conferences, and diary entries
        will appear automatically. No login required — the URL is private to you.
      </p>

      {isLoading ? (
        <div className="h-10 animate-pulse rounded-xl bg-muted/40" />
      ) : data ? (
        <>
          <div className="flex items-center gap-2.5 rounded-xl border border-border/50 bg-muted/20 px-4 py-3">
            <LinkIcon size={13} className="shrink-0 text-muted-foreground/60" />
            <span className="flex-1 truncate font-mono text-xs text-muted-foreground select-all">
              {data.url}
            </span>
            <button
              type="button"
              onClick={copyUrl}
              className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-[10px] font-medium text-foreground transition hover:bg-muted"
            >
              {copied ? (
                <><Check size={10} className="text-emerald-600 dark:text-emerald-400" /> Copied</>
              ) : (
                <><Copy size={10} /> Copy URL</>
              )}
            </button>
          </div>

          {data.expiresAt && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Feed expires:{' '}
              <span className="font-semibold text-foreground">{formatExpiry(data.expiresAt)}</span>
              {' '}&mdash; regenerate before this date to maintain your subscription.
            </p>
          )}

          <div className="mt-4 rounded-xl border border-border/50 bg-muted/10 px-5 py-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              How to subscribe
            </p>
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              <li><strong className="text-foreground">Google Calendar:</strong> Other calendars → Add by URL → paste the URL above</li>
              <li><strong className="text-foreground">Apple Calendar:</strong> File → New Calendar Subscription → paste the URL above</li>
              <li><strong className="text-foreground">Outlook:</strong> Add calendar → Subscribe from web → paste the URL above</li>
            </ul>
          </div>

          {confirmRegen ? (
            <div className="mt-4 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4">
              <div className="flex items-start gap-2">
                <Warning size={14} weight="fill" className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="text-xs text-amber-800 dark:text-amber-300">
                  <p className="font-semibold">This will break all existing subscriptions and reset the 365-day expiry.</p>
                  <p className="mt-0.5">Any calendar app currently subscribed to the old URL will stop updating immediately. You will need to remove the old subscription and re-add the new URL in every calendar app.</p>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => setConfirmRegen(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  variant="primary"
                  disabled={regenMutation.isPending}
                  onClick={() => regenMutation.mutate()}
                >
                  {regenMutation.isPending ? 'Regenerating…' : 'Yes, regenerate'}
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmRegen(true)}
              className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition"
            >
              <ArrowsClockwise size={12} />
              Regenerate URL (breaks all existing subscriptions, resets 365-day expiry)
            </button>
          )}
        </>
      ) : null}
    </div>
  );
}

// ─── Google two-way sync section (LIVE) ───────────────────────

function formatSyncTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

// One provider's connect → enable → syncing row. Google and Outlook share this exactly;
// only the icon/labels and the OAuth handshake differ. The parent owns the single status
// query so both rows read from one poll.
interface ProviderConfig {
  id: CalendarSyncProviderId;
  oauth: 'google' | 'microsoft';
  name: string;
  fallbackSub: string;
  connectLabel: string;
  icon: React.ReactNode;
}

function ProviderSyncRow({
  cfg, status, isLoading, connecting,
  onConnect, onReconnect, onEnable, onDisable, onResync,
  enabling, disabling, resyncing,
}: {
  cfg: ProviderConfig;
  status: CalendarSyncProviderStatus | undefined;
  isLoading: boolean;
  connecting: boolean;
  onConnect: () => void;
  onReconnect: () => void;
  onEnable: () => void;
  onDisable: () => void;
  onResync: () => void;
  enabling: boolean;
  disabling: boolean;
  resyncing: boolean;
}) {
  return (
    <div className="rounded-xl border border-border/50 px-4 py-3.5">
      {/* Nothing here could shrink and there was no breakpoint, so on a phone the account name
          pushed the badge and its button off the right edge. Stacked below sm; `items-start`
          keeps the buttons their own width instead of stretching the full row. */}
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-white shadow-sm">
            {cfg.icon}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">{cfg.name}</div>
            <div className="mt-0.5 truncate text-xs text-muted-foreground">
              {(status?.connected || status?.needsReconnect) && status?.emailAddress ? status.emailAddress : cfg.fallbackSub}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="h-7 w-24 animate-pulse rounded-lg bg-muted/40" />
        ) : status?.needsReconnect ? (
          // The connection exists but its sign-in expired or was revoked. Offering "Connect" here
          // read as though nothing had ever been connected.
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-500/15 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
              <Warning size={9} weight="bold" /> Reconnect needed
            </span>
            <Button size="xs" variant="primary" disabled={connecting} onClick={onReconnect}>
              {connecting ? 'Redirecting…' : 'Reconnect'}
            </Button>
          </div>
        ) : !status?.connected ? (
          <Button size="xs" variant="primary" disabled={connecting} onClick={onConnect}>
            {connecting ? 'Redirecting…' : cfg.connectLabel}
          </Button>
        ) : !status.syncEnabled ? (
          <Button size="xs" variant="primary" disabled={enabling} onClick={onEnable}>
            {enabling ? 'Enabling…' : 'Enable two-way sync'}
          </Button>
        ) : (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-500/15 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              <Check size={9} weight="bold" /> Syncing
            </span>
            <Button size="xs" variant="outline" disabled={disabling} onClick={onDisable}>
              {disabling ? 'Turning off…' : 'Turn off'}
            </Button>
          </div>
        )}
      </div>

      {status?.needsReconnect && (
        <p className="mt-3 border-t border-border/40 pt-3 text-[11px] text-muted-foreground">
          The sign-in for this account has expired or was revoked. Reconnect with the same account to resume syncing.
        </p>
      )}

      {status?.connected && !status.needsReconnect && status.syncEnabled && (
        <div className="mt-3 flex items-center justify-between border-t border-border/40 pt-3">
          <div className="text-[11px] text-muted-foreground">
            {status.lastSyncAt
              ? <>Last synced <span className="font-semibold text-foreground">{formatSyncTime(status.lastSyncAt)}</span></>
              : 'First sync in progress…'}
            {status.lastSyncError && (
              <span className="ml-2 inline-flex items-center gap-1 text-amber-700 dark:text-amber-300">
                <Warning size={11} weight="fill" /> {status.lastSyncError}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onResync}
            disabled={resyncing}
            className="flex items-center gap-1.5 text-[11px] text-muted-foreground transition hover:text-foreground disabled:opacity-50"
          >
            <ArrowsClockwise size={11} />
            {resyncing ? 'Resyncing…' : 'Resync now'}
          </button>
        </div>
      )}
    </div>
  );
}

const SYNC_PROVIDERS: ProviderConfig[] = [
  {
    id: 'google_calendar', oauth: 'google',
    name: 'Google Calendar', fallbackSub: 'Two-way sync with your Google account',
    connectLabel: 'Connect Google',
    icon: <GoogleLogo size={16} weight="bold" className="text-red-500" />,
  },
  {
    id: 'microsoft_365', oauth: 'microsoft',
    name: 'Microsoft Outlook', fallbackSub: 'Two-way sync with your Microsoft 365 account',
    connectLabel: 'Connect Outlook',
    icon: <span className="text-xs font-bold text-blue-600 dark:text-blue-400">O</span>,
  },
];

function CalendarSyncRows() {
  const qc = useQueryClient();
  const [connecting, setConnecting] = useState<CalendarSyncProviderId | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['calendar-sync-status'],
    queryFn: fetchCalendarSyncStatus,
    // Poll while ANY provider is on so "last synced" stays fresh after the 5-min cycle.
    refetchInterval: (q) => (q.state.data?.providers?.some((p) => p.syncEnabled) ? 60_000 : false),
  });

  const enableMut = useMutation({
    mutationFn: enableCalendarSync,
    onSuccess: (d) => qc.setQueryData(['calendar-sync-status'], d),
  });
  const disableMut = useMutation({
    mutationFn: disableCalendarSync,
    onSuccess: (d) => qc.setQueryData(['calendar-sync-status'], d),
  });
  const resyncMut = useMutation({
    mutationFn: resyncCalendar,
    onSuccess: (d) => qc.setQueryData(['calendar-sync-status'], d),
  });

  // Returns here, not to the inbox. With a mailboxId it is a reconnect of that account.
  async function connect(cfg: ProviderConfig, mailboxId?: string) {
    setConnecting(cfg.id);
    try {
      const url = await beginOAuthConnect(cfg.oauth, { mailboxId, returnTo: 'calendar' });
      window.location.href = url;
    } catch (err) {
      setConnecting(null);
      toast.error(getApiErrorMessage(err, `Could not start the ${cfg.name} connection`));
    }
  }

  // A failed status load must not render "Connect Google" as though nothing were connected.
  if (isError) {
    return <QueryErrorState compact what="your calendar connections" onRetry={() => void refetch()} />;
  }

  return (
    <div className="space-y-2.5">
      {SYNC_PROVIDERS.map((cfg) => {
        const status = data?.providers.find((p) => p.provider === cfg.id);
        return (
          <ProviderSyncRow
            key={cfg.id}
            cfg={cfg}
            status={status}
            isLoading={isLoading}
            connecting={connecting === cfg.id}
            onConnect={() => connect(cfg)}
            onReconnect={() => connect(cfg, status?.mailboxId ?? undefined)}
            onEnable={() => enableMut.mutate(cfg.id)}
            onDisable={() => disableMut.mutate(cfg.id)}
            onResync={() => resyncMut.mutate(cfg.id)}
            enabling={enableMut.isPending && enableMut.variables === cfg.id}
            disabling={disableMut.isPending && disableMut.variables === cfg.id}
            resyncing={resyncMut.isPending && resyncMut.variables === cfg.id}
          />
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════

export default function CalendarSharingPage() {
  const navigate = useNavigate();

  return (
    <div className="mx-auto max-w-2xl pb-20">
      {/* Reached from Settings → Diary & Calendar, which is a page of its own rather than a
          child route, so there was no way back except the browser control. */}
      <BackButton onClick={() => navigate(-1)} />

      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Calendar Sharing</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Share your availability with instructing solicitors and keep your diary in sync.
        </p>
      </div>

      <OAuthReturnNotice connectedText="Turn on two-way sync below to start syncing your calendar." />

      <div className="space-y-6">

        {/* 1. iCal subscription — LIVE */}
        <IcalSection />

        {/* 2. External calendar sync — Google LIVE, Outlook coming */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <SectionHead
            icon={<CalendarBlank size={14} weight="bold" />}
            title="Two-Way Calendar Sync"
            desc="Hearings and diary push out to your calendar; personal events flow back in — both ways"
          />

          <p className="mb-4 text-sm text-muted-foreground leading-relaxed">
            Connect your Google or Microsoft account once (the same login as email) and Jurivoxa
            keeps your diary and that calendar in step — create, move, or delete in either place.
            Court hearings stay protected: deleting one externally restores it here.
          </p>

          <CalendarSyncRows />
        </div>

        {/* 3. Availability settings (live link) */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <SectionHead
            icon={<CalendarCheck size={14} weight="bold" />}
            title="Availability Settings"
            desc="Mark specific days as available, busy, or on leave"
          />

          <p className="mb-4 text-sm text-muted-foreground leading-relaxed">
            Use the availability page to mark your days, block out leave periods, and indicate
            when you are available to accept new instructions.
          </p>

          <Link
            to="/barrister/availability"
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-primary/90"
          >
            <Clock size={13} weight="bold" />
            Manage availability
            <ArrowSquareOut size={11} className="opacity-70" />
          </Link>
        </div>

      </div>
    </div>
  );
}
